import type {
  AdminProgramme,
  ObjectiveStrength,
  ProgrammeArtwork,
  ProgrammePatch,
  Rights,
  TermRole,
  Vocabulary,
} from '@/api/types';
import { toDate, toIso } from '@/lib/format';

export interface FormTerm {
  term_id: string;
  vocabulary: Vocabulary;
  role: TermRole;
}

export interface FormObjective {
  objective_term_id: string;
  strength: ObjectiveStrength;
  note: string;
}

export interface ProgrammeFormValues {
  title: string;
  slug: string;
  synopsis_short: string;
  synopsis: string;
  creator_id: string | null;
  /** '' while unset (NumberInput's empty value). */
  min_age: number | '';
  max_age: number | '';
  age_rating: number | null;
  artwork: ProgrammeArtwork;
  search_tags: string[];
  editorial_priority: number;
  discovery_eligible: boolean;
  territories: string[];
  rights: Rights;
  available_from: Date | null;
  available_to: Date | null;
  publish_at: Date | null;
  unpublish_at: Date | null;
  terms: FormTerm[];
  objectives: FormObjective[];
  preview_video_id: string | null;
  preview_approved: boolean;
}

export function toFormValues(p: AdminProgramme): ProgrammeFormValues {
  return {
    title: p.title,
    slug: p.slug,
    synopsis_short: p.synopsis_short,
    synopsis: p.synopsis,
    creator_id: p.creator?.id ?? null,
    min_age: p.min_age ?? '',
    max_age: p.max_age ?? '',
    age_rating: p.age_rating ?? null,
    artwork: {
      poster: p.artwork.poster ?? null,
      landscape: p.artwork.landscape ?? null,
      thumb: p.artwork.thumb ?? null,
      title_art: p.artwork.title_art ?? null,
    },
    search_tags: [...p.search_tags],
    editorial_priority: p.editorial_priority,
    discovery_eligible: p.discovery_eligible,
    territories: [...p.territories],
    rights: { ...p.rights },
    available_from: toDate(p.available_from),
    available_to: toDate(p.available_to),
    publish_at: toDate(p.publish_at),
    unpublish_at: toDate(p.unpublish_at),
    terms: p.terms.map((t) => ({ term_id: t.term_id, vocabulary: t.vocabulary, role: t.role })),
    objectives: p.objectives.map((o) => ({
      objective_term_id: o.objective_term_id,
      strength: o.strength,
      note: o.note ?? '',
    })),
    preview_video_id: p.preview.video?.id ?? null,
    preview_approved: p.preview.approved,
  };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** The PATCH body for the fields that changed between `initial` and `values`. */
export function buildPatch(
  initial: ProgrammeFormValues,
  values: ProgrammeFormValues,
): ProgrammePatch {
  const patch: ProgrammePatch = {};
  const changed = <K extends keyof ProgrammeFormValues>(k: K) => !same(initial[k], values[k]);

  if (changed('title')) patch.title = values.title.trim();
  if (changed('slug')) patch.slug = values.slug.trim();
  if (changed('synopsis_short')) patch.synopsis_short = values.synopsis_short;
  if (changed('synopsis')) patch.synopsis = values.synopsis;
  if (changed('creator_id')) patch.creator_id = values.creator_id;
  const age = (v: number | '') => (v === '' ? null : Number(v));
  if (changed('min_age')) patch.min_age = age(values.min_age);
  if (changed('max_age')) patch.max_age = age(values.max_age);
  if (changed('age_rating')) patch.age_rating = values.age_rating;
  // The API replaces the whole artwork object.
  if (changed('artwork')) patch.artwork = { ...values.artwork };
  if (changed('search_tags')) patch.search_tags = values.search_tags;
  if (changed('editorial_priority')) patch.editorial_priority = values.editorial_priority;
  if (changed('discovery_eligible')) patch.discovery_eligible = values.discovery_eligible;
  if (changed('territories')) patch.territories = values.territories;
  if (changed('rights')) patch.rights = values.rights;
  for (const k of ['available_from', 'available_to', 'publish_at', 'unpublish_at'] as const) {
    if (toIso(initial[k]) !== toIso(values[k])) patch[k] = toIso(values[k]);
  }
  if (changed('terms')) patch.terms = values.terms.map(({ term_id, role }) => ({ term_id, role }));
  if (changed('objectives'))
    patch.objectives = values.objectives.map((o) => ({
      objective_term_id: o.objective_term_id,
      strength: o.strength,
      note: o.note.trim() || null,
    }));
  if (changed('preview_video_id')) patch.preview_video_id = values.preview_video_id;
  if (changed('preview_approved')) patch.preview_approved = values.preview_approved;
  return patch;
}

/** Client-side checks that block saving (the server re-validates). */
export function validateForm(v: ProgrammeFormValues): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!v.title.trim()) errors.title = 'A title is required.';
  if (v.min_age !== '' && v.max_age !== '' && v.min_age > v.max_age)
    errors.max_age = 'Maximum age must be at least the minimum age.';
  if (v.available_from && v.available_to && v.available_from >= v.available_to)
    errors.available_to = 'Must be after “available from”.';
  if (v.publish_at && v.unpublish_at && v.publish_at >= v.unpublish_at)
    errors.unpublish_at = 'Must be after the publish date.';
  return errors;
}

export type EditorTab =
  | 'details'
  | 'artwork'
  | 'classification'
  | 'episodes'
  | 'rights'
  | 'preview';

export const TABS: { value: EditorTab; label: string }[] = [
  { value: 'details', label: 'Details' },
  { value: 'artwork', label: 'Artwork' },
  { value: 'classification', label: 'Classification' },
  { value: 'episodes', label: 'Episodes & video' },
  { value: 'rights', label: 'Rights & schedule' },
  { value: 'preview', label: 'Preview' },
];

/** Which tab fixes a validation issue's `field`. */
export function tabForField(field: string): EditorTab {
  if (field.startsWith('artwork')) return 'artwork';
  if (field.startsWith('terms') || field.startsWith('objectives') || field === 'search_tags')
    return field === 'search_tags' ? 'details' : 'classification';
  if (field.startsWith('episodes') || field.startsWith('seasons') || field.startsWith('video'))
    return 'episodes';
  if (field.startsWith('preview')) return 'preview';
  if (
    field.startsWith('available') ||
    field.startsWith('territor') ||
    field.startsWith('rights') ||
    field.startsWith('publish') ||
    field.startsWith('unpublish')
  )
    return 'rights';
  return 'details';
}
